import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  API_BASE_URL,
  getAuthHeaders,
  getProjectUserSummary,
  saveProjectUserSummary,
  submitProjectSummaryToModel,
  updateProjectSummaryFromSession,
} from '../utils/api';
import './ProjectSummaryPage.css';

/**
 * Per-user, per-project summarization persisted across chats and injected into the model system prompt.
 */
const ProjectSummaryPage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const returnSessionId = searchParams.get('session') || '';

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [updatingFromChat, setUpdatingFromChat] = useState(false);
  const [error, setError] = useState('');
  const [banner, setBanner] = useState('');

  const [content, setContent] = useState('');
  const [maxContextTokens, setMaxContextTokens] = useState(4000);
  const [isPublic, setIsPublic] = useState(false);
  const [projectName, setProjectName] = useState('');

  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setError('');
    try {
      const headers = await getAuthHeaders();
      const presp = await fetch(`${API_BASE_URL}/casc/v1/api/chat/project/get`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ projectId }),
      });
      const pjson = await presp.json().catch(() => ({}));
      if (presp.ok && pjson?.success && pjson.data?.name) {
        setProjectName(pjson.data.name);
      }

      const row = await getProjectUserSummary(projectId);
      if (row) {
        setContent(row.content || '');
        setMaxContextTokens(typeof row.max_context_tokens === 'number' ? row.max_context_tokens : 4000);
        setIsPublic(row.is_public === true);
      } else {
        setContent('');
        setMaxContextTokens(4000);
        setIsPublic(false);
      }
    } catch (e) {
      setError(e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (location.state?.summaryUpdatedFromChat) {
      setBanner(
        'Project summary updated from this chat. Review the text below; new assistant replies will use it within the token budget.'
      );
      navigate(`${location.pathname}${location.search}`, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, location.search, navigate]);

  const onSave = async () => {
    if (!projectId) return;
    setSaving(true);
    setError('');
    setBanner('');
    try {
      await saveProjectUserSummary(projectId, {
        content,
        max_context_tokens: maxContextTokens,
        is_public: isPublic,
      });
      setBanner('Summary saved.');
    } catch (e) {
      setError(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const onSubmitToModel = async () => {
    if (!projectId) return;
    setSubmitting(true);
    setError('');
    setBanner('');
    try {
      await saveProjectUserSummary(projectId, {
        content,
        max_context_tokens: maxContextTokens,
        is_public: isPublic,
      });
      await submitProjectSummaryToModel(projectId, returnSessionId || undefined);
      setBanner(
        returnSessionId
          ? 'Summary submitted. A notice was added to this chat; new assistant replies will use this context.'
          : 'Summary submitted. New messages in this project’s chats will use this context.'
      );
    } catch (e) {
      setError(e.message || 'Submit failed');
    } finally {
      setSubmitting(false);
    }
  };

  const onUpdateFromChat = async () => {
    if (!projectId || !returnSessionId) return;
    if (
      !window.confirm(
        'Compress the linked chat into this project summary?\n\n' +
          'This merges the conversation into the saved summary via the model. Review the result before relying on it.'
      )
    ) {
      return;
    }
    setUpdatingFromChat(true);
    setError('');
    setBanner('');
    try {
      const result = await updateProjectSummaryFromSession(projectId, returnSessionId);
      const next = result?.summary?.content;
      if (typeof next === 'string') setContent(next);
      if (typeof result?.summary?.max_context_tokens === 'number') {
        setMaxContextTokens(result.summary.max_context_tokens);
      }
      setBanner(
        'Summary updated from the linked chat. Review the text; new assistant replies will use it within the token budget.'
      );
    } catch (e) {
      setError(e.message || 'Update from chat failed');
    } finally {
      setUpdatingFromChat(false);
    }
  };

  const backTarget = returnSessionId && projectId
    ? `/p/${projectId}/c/${returnSessionId}`
    : (projectId ? `/p/${projectId}` : '/projects');

  const busy = saving || submitting || updatingFromChat;

  return (
    <div className="project-summary-page">
      <div className="project-summary-toolbar">
        <button type="button" className="btn ghost" onClick={() => navigate(backTarget)}>
          <i className="fas fa-arrow-left" /> Back
        </button>
        <h2 className="project-summary-title">
          Project summary{projectName ? ` · ${projectName}` : ''}
        </h2>
      </div>

      <p className="project-summary-lede">
        This text is stored per project for your account and injected into the assistant’s system context (within the token budget below).
        It persists across chats in this project. Use <strong>Update from this chat</strong> (when opened from a chat) or the
        chat header <strong>Update summary</strong> button to compress the conversation into this field — that is manual only.
        Say <strong>“this is right”</strong> or <strong>“please update”</strong> in chat to checkpoint the summary row;
        use <strong>Submit to model</strong> after editing to refresh what the next replies use.
      </p>

      {loading && (
        <div className="subtle"><i className="fas fa-spinner fa-pulse" /> Loading…</div>
      )}
      {error && (
        <div className="error-banner" role="alert"><i className="fas fa-exclamation-triangle" /> {error}</div>
      )}
      {banner && (
        <div className="ok-banner" role="status">{banner}</div>
      )}

      {!loading && (
        <>
          <div className="form-group">
            <label htmlFor="sum-content">Summary content</label>
            <textarea
              id="sum-content"
              className="project-summary-textarea"
              rows={18}
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Key facts, ICP, decisions, tables, constraints…"
            />
          </div>

          <div className="project-summary-row">
            <div className="form-group">
              <label htmlFor="sum-tokens">Max context tokens (injection budget)</label>
              <input
                id="sum-tokens"
                type="number"
                min={200}
                max={32000}
                step={100}
                value={maxContextTokens}
                onChange={(e) => setMaxContextTokens(Math.max(200, Math.min(32000, parseInt(e.target.value, 10) || 4000)))}
              />
              <span className="hint">Rough limit for how much of this summary is kept when building the model prompt (200–32000).</span>
            </div>

            <div className="form-group project-summary-visibility">
              <label>Visibility</label>
              <div className="toggle-row">
                <button
                  type="button"
                  className={`btn btn-sm ${!isPublic ? 'primary' : 'ghost'}`}
                  onClick={() => setIsPublic(false)}
                >
                  <i className="fas fa-lock" /> Private
                </button>
                <button
                  type="button"
                  className={`btn btn-sm ${isPublic ? 'primary' : 'ghost'}`}
                  onClick={() => setIsPublic(true)}
                >
                  <i className="fas fa-globe" /> Public
                </button>
              </div>
              <span className="hint">Public lets teammates see this summary metadata in future UI; the assistant always uses your own summary when you chat.</span>
            </div>
          </div>

          <div className="project-summary-actions">
            {returnSessionId ? (
              <button
                type="button"
                className="btn secondary"
                onClick={onUpdateFromChat}
                disabled={busy}
                title="Compress the linked chat into this summary"
              >
                {updatingFromChat ? (
                  <><i className="fas fa-spinner fa-pulse" /> Updating from chat…</>
                ) : (
                  <><i className="fas fa-compress" /> Update from this chat</>
                )}
              </button>
            ) : null}
            <button type="button" className="btn primary" onClick={onSave} disabled={busy}>
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button type="button" className="btn secondary" onClick={onSubmitToModel} disabled={busy}>
              {submitting ? 'Submitting…' : 'Submit to model'}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default ProjectSummaryPage;
